import { Alert, AlertType } from '@design/components/alert/Alert';
import { useRunScheduledQueryNow } from '@terminal/react-query-hooks/alfred/useScheduledQueryMutations';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { useSubscribeToRunResult } from '@terminal/views/alfred/research/hooks/useSubscribeToRunResult';
import { useTranslation } from 'react-i18next';

export type ScheduledQueryToastVariant = 'created' | 'updated';

const SCHEDULED_TOAST_KEYS: Record<
  ScheduledQueryToastVariant,
  { title: string; message: string }
> = {
  created: {
    title: 'toastTaskScheduledTitle',
    message: 'toastTaskScheduledMessage',
  },
  updated: {
    title: 'toastTaskUpdatedTitle',
    message: 'toastTaskUpdatedMessage',
  },
};

export const useScheduledQueryToast = () => {
  const { t } = useTranslation(['chat']);
  const { showToast, removeToast } = useToasts(state => ({
    showToast: state.showToast,
    removeToast: state.removeToast,
  }));
  const { mutate: runNow } = useRunScheduledQueryNow();
  const { subscribe } = useSubscribeToRunResult();

  const showAlertToast = (type: AlertType, title: string, message: string) => {
    const toastId = `scheduled-query-toast-${Date.now()}`;
    showToast({
      id: toastId,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          type={type}
          title={title}
          message={message}
          onClose={() => removeToast(toastId)}
        />
      ),
    });
  };

  const showScheduledToast = (
    taskId: string,
    variant: ScheduledQueryToastVariant
  ) => {
    const { title: titleKey, message: messageKey } =
      SCHEDULED_TOAST_KEYS[variant];
    const toastId = `task-toast-${Date.now()}`;

    showToast({
      id: toastId,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          type={AlertType.Success}
          title={t(`chat:scheduledQuerySettings.${titleKey}`)}
          message={t(`chat:scheduledQuerySettings.${messageKey}`)}
          onClose={() => removeToast(toastId)}
          primaryBtn={{
            label: t('chat:scheduledQuerySettings.runNow'),
            onClick: () => {
              removeToast(toastId);
              const unsubscribe = subscribe(taskId);
              runNow(taskId, { onError: () => unsubscribe() });
            },
          }}
        />
      ),
    });
  };

  const showPromptDeletedToast = () => {
    showAlertToast(
      AlertType.Success,
      t('chat:promptLibrary.deletePromptSuccessTitle'),
      t('chat:promptLibrary.deletePromptSuccessMessage')
    );
  };

  const showPromptDeleteFailedToast = () => {
    showAlertToast(
      AlertType.Error,
      t('chat:promptLibrary.deletePromptFailedTitle'),
      t('chat:promptLibrary.deletePromptFailedMessage')
    );
  };

  return {
    showScheduledToast,
    showPromptDeletedToast,
    showPromptDeleteFailedToast,
  };
};
